import { srgbToLab, type Illuminant, type SRGB } from '../color/index.js';
import type { ReferenceCard, ReferencePatch } from './types.js';

/** Input for {@link buildRelativeCard}: one baseline observation per patch. */
export interface BaselineSample {
  id: string;
  name: string;
  /** What the camera saw for this patch in the baseline frame. */
  srgb: SRGB;
}

/**
 * Build a reference card from a baseline capture.
 *
 * The resulting targets are "whatever the camera saw under the baseline
 * lighting". Calibrating against this card makes later readings consistent with
 * that baseline, which is what makes two sessions comparable — but it says
 * nothing about the true colour of anything, hence `provenance: 'relative'`.
 */
export function buildRelativeCard(options: {
  id: string;
  name: string;
  samples: BaselineSample[];
  illuminant?: Illuminant;
  notes?: string;
}): ReferenceCard {
  const { id, name, samples, illuminant = 'D65', notes } = options;

  if (samples.length === 0) {
    throw new Error('A reference card needs at least one patch.');
  }
  assertUniqueIds(samples.map((s) => s.id));

  const patches: ReferencePatch[] = samples.map((sample) => ({
    id: sample.id,
    name: sample.name,
    lab: srgbToLab(sample.srgb, illuminant),
  }));

  return {
    id,
    name,
    provenance: 'relative',
    illuminant,
    patches,
    ...(notes === undefined ? {} : { notes }),
  };
}

/** A verified patch specification, as supplied by a manufacturer or measured instrument. */
export interface AbsolutePatchSpec {
  id: string;
  name: string;
  /** Null until a verified value exists. */
  lab: { L: number; a: number; b: number } | null;
}

/**
 * Build a reference card from verified Lab values.
 *
 * This is the swap-in path for absolute calibration. It refuses to build a card
 * from an incomplete spec rather than substituting a guess: a card that claims
 * `provenance: 'absolute'` while carrying invented numbers would be worse than
 * no card at all, because every downstream reading would inherit the claim.
 */
export function buildAbsoluteCard(options: {
  id: string;
  name: string;
  specs: AbsolutePatchSpec[];
  illuminant: Illuminant;
  notes?: string;
}): ReferenceCard {
  const { id, name, specs, illuminant, notes } = options;

  if (specs.length === 0) {
    throw new Error('A reference card needs at least one patch.');
  }
  assertUniqueIds(specs.map((s) => s.id));

  const missing = specs.filter((s) => s.lab === null).map((s) => `${s.name} (${s.id})`);
  if (missing.length > 0) {
    throw new Error(
      `Cannot build an absolute reference card: no verified Lab values for ${missing.join(', ')}. ` +
        'Supply measured or manufacturer-published values, or use buildRelativeCard instead.',
    );
  }

  return {
    id,
    name,
    provenance: 'absolute',
    illuminant,
    patches: specs.map((spec) => ({
      id: spec.id,
      name: spec.name,
      lab: spec.lab as { L: number; a: number; b: number },
    })),
    ...(notes === undefined ? {} : { notes }),
  };
}

function assertUniqueIds(ids: string[]): void {
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) throw new Error(`Duplicate patch id: ${id}`);
    seen.add(id);
  }
}
